import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dropdown } from 'primereact/dropdown';
import type { EChartsCoreOption } from 'echarts';

import { RootState } from '../../../store';
import { alertService } from '../../../shared/services/alertService';
import {
  useLazyGetNewCounsellingQuery,
  useLazyGetCounsellingStatusQuery,
  useLazyGetEduDistDashboardQuery,
  useLazyGetStateQuery,
  useLazyGetAllEduDistQuery,
} from '../counselling.services';
import { useECharts } from './useECharts';

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Dropdown option for the counselling selector (Angular CounsellingDropDown). */
interface CounsellingDropDown {
  label: string;
  value: any;
  directorate: any;
}

/** A row from getCounsellingStatus().result (dashboard counts). */
interface DashboardCountRow {
  CnsId: any;
  Candidates: any;
  OrderCount: any;
  NotWillingCount: any;
  AbsentCount: any;
}

/** Stacked-bar datum: percentage string for the label, absolute total for tooltip. */
interface BarDatum {
  value: string;
  total: number;
}

/** Pie-chart datum. */
interface PieDatum {
  value: number;
  name: string;
}

/** String-safe compare — Angular mixed `==` string/number comparisons. */
const eq = (a: any, b: any) => String(a ?? '') === String(b ?? '');

/**
 * Migrated from Angular `CounsellingDashboardComponent`
 * (src/app/views/pages/counselling/counselling-dashboard).
 *
 * Role-filtered counselling picker driving three ECharts:
 *  - gauge (candidates taken vs total)
 *  - pie (completed / not-willing / absent / pending)
 *  - stacked bar (state user type 5 only: edu-district or district wise)
 */
const CounsellingDashboard = () => {
  const session = useSelector((state: RootState) => state.session);
  const userType = String(session.usertypeid ?? '');
  const userType1 = String(session.emisUsertype1 ?? '');

  // ── Services (lazy — Angular fetched imperatively) ─────────────────────────
  const [triggerNewCounselling] = useLazyGetNewCounsellingQuery();
  const [triggerCounsellingStatus] = useLazyGetCounsellingStatusQuery();
  const [triggerEduDistDashboard] = useLazyGetEduDistDashboardQuery();
  const [triggerState] = useLazyGetStateQuery();
  const [triggerAllEduDist] = useLazyGetAllEduDistQuery();

  // ── State (Angular component fields) ───────────────────────────────────────
  const [counsellingList, setCounsellingList] = useState<CounsellingDropDown[]>(
    []
  );
  const [counselling, setCounselling] = useState<any>(null);
  const [dashboardCount, setDashboardCount] = useState<DashboardCountRow[]>([]);
  const [pieChartData, setPieChartData] = useState<PieDatum[]>([]);
  const [totalTaken, setTotalTaken] = useState(0);
  const [lastDigit, setLastDigit] = useState(0);

  const [totalPendingArr, setTotalPendingArr] = useState<BarDatum[]>([]);
  const [totalOrderArr, setTotalOrderArr] = useState<BarDatum[]>([]);
  const [totalNotWillingArr, setTotalNotWillingArr] = useState<BarDatum[]>([]);
  const [totalAbsentArr, setTotalAbsentArr] = useState<BarDatum[]>([]);
  const [axisArr, setAxisArr] = useState<string[]>([]);
  const [chartHeader, setChartHeader] = useState('');

  const [allDistrict, setAllDistrict] = useState<any[]>([]);
  const [allEduDist, setAllEduDist] = useState<any[]>([]);

  const isState5 = eq(userType, '5');

  // ── handleCounselling: fetch status, build pie + gauge, and (type 5) bars ──
  const handleCounselling = useCallback(
    async (
      csncId: any,
      list: CounsellingDropDown[],
      eduDist: any[],
      districtList: any[]
    ) => {
      const counseling = list.filter((x) => eq(x.value, csncId));

      // Reset the bar accumulators for this run.
      const axis: string[] = [];
      const pendingArr: BarDatum[] = [];
      const orderArr: BarDatum[] = [];
      const notWillingArr: BarDatum[] = [];
      const absentArr: BarDatum[] = [];

      try {
        const res: any = await triggerCounsellingStatus().unwrap();
        if (res?.dataStatus) {
          setCounselling(csncId);
          const counts: DashboardCountRow[] = (res.result ?? []).filter(
            (ftr: any) => eq(ftr.CnsId, csncId)
          );
          setDashboardCount(counts);

          if (counts.length > 0) {
            const c = counts[0];
            const candidates = +c.Candidates;
            const order = +c.OrderCount;
            const notWilling = +c.NotWillingCount;
            const absent = +c.AbsentCount;

            setPieChartData([
              { value: order, name: 'Completed' },
              { value: notWilling, name: 'Not Willing / Relinguish' },
              { value: absent, name: 'Absent' },
              {
                value: candidates - (order + notWilling + absent),
                name: 'Pending',
              },
            ]);

            const taken = notWilling + absent + order;
            setTotalTaken(taken);
            setLastDigit(candidates > 10 ? candidates % 10 : 0);
          } else {
            setPieChartData([]);
            setTotalTaken(0);
            setLastDigit(0);
          }
        }
      } catch {
        alertService.error('Failed to load counselling status');
      }

      // Bar chart (state user type 5 only).
      if (isState5 && counseling.length > 0) {
        try {
          const res: any = await triggerEduDistDashboard({
            counsellingId: csncId,
            directorate: counseling[0].directorate,
          }).unwrap();

          if (res?.dataStatus) {
            if (eq(counseling[0].directorate, 1)) {
              // Education-district wise.
              const filtArr = (res.result ?? []).filter((x: any) =>
                eq(x.directorate, 1)
              );
              setChartHeader('Education District wise Data');
              const filterEduDist = eduDist.filter((x: any) =>
                eq(x.directoRate, 1)
              );
              filterEduDist.forEach((eduDIst: any) => {
                let tot = 0;
                filtArr.forEach((x: any) => {
                  if (eq(x.edu_dist_id, eduDIst.edudistId)) {
                    const pending =
                      +x.TotalCount -
                      (+x.OrderCount + +x.NotWillingCount + +x.AbsentCount);
                    tot = +x.TotalCount;
                    pendingArr.push({
                      value:
                        tot > 0 ? ((pending / tot) * 100).toFixed(1) : '0.0',
                      total: pending,
                    });
                    orderArr.push({
                      value:
                        tot > 0
                          ? ((+x.OrderCount / tot) * 100).toFixed(1)
                          : '0.0',
                      total: +x.OrderCount,
                    });
                    notWillingArr.push({
                      value:
                        tot > 0
                          ? ((+x.NotWillingCount / tot) * 100).toFixed(1)
                          : '0.0',
                      total: +x.NotWillingCount,
                    });
                    absentArr.push({
                      value:
                        tot > 0
                          ? ((+x.AbsentCount / tot) * 100).toFixed(1)
                          : '0.0',
                      total: +x.AbsentCount,
                    });
                    axis.push(eduDIst.edudistName);
                  }
                });
                if (tot === 0) {
                  pendingArr.push({ value: '0.0', total: 0 });
                  orderArr.push({ value: '0.0', total: 0 });
                  notWillingArr.push({ value: '0.0', total: 0 });
                  absentArr.push({ value: '0.0', total: 0 });
                  axis.push(eduDIst.edudistName);
                }
              });
            } else {
              // District wise.
              const filtArr = res.result ?? [];
              setChartHeader('District wise Data');
              districtList.forEach((dis: any) => {
                let total = 0;
                let orderCopy = 0;
                let notWilling = 0;
                let absent = 0;
                filtArr.forEach((x: any) => {
                  if (eq(dis.id, x.district_id)) {
                    total += +x.TotalCount;
                    orderCopy += +x.OrderCount;
                    notWilling += +x.NotWillingCount;
                    absent += +x.AbsentCount;
                  }
                });
                const pending = total - (orderCopy + notWilling + absent);
                pendingArr.push({
                  value:
                    total > 0 ? ((pending / total) * 100).toFixed(1) : '0.0',
                  total: pending,
                });
                orderArr.push({
                  value:
                    total > 0 ? ((orderCopy / total) * 100).toFixed(1) : '0.0',
                  total: orderCopy,
                });
                notWillingArr.push({
                  value:
                    total > 0 ? ((notWilling / total) * 100).toFixed(1) : '0.0',
                  total: notWilling,
                });
                absentArr.push({
                  value:
                    total > 0 ? ((absent / total) * 100).toFixed(1) : '0.0',
                  total: absent,
                });
                axis.push(dis.district_name);
              });
            }
          }
        } catch {
          // Angular calls barChart() in the error branch too; here the empty
          // arrays produce an empty chart which is the same effect.
        }

        setAxisArr(axis);
        setTotalPendingArr(pendingArr);
        setTotalOrderArr(orderArr);
        setTotalNotWillingArr(notWillingArr);
        setTotalAbsentArr(absentArr);
      }
    },
    [isState5, triggerCounsellingStatus, triggerEduDistDashboard]
  );

  // ── GetAllNewCounselling: role filter → dropdown list → auto-select first ──
  const getAllNewCounselling = useCallback(async () => {
    // State-5 also needs districts + edu districts for the bar chart.
    let eduDist: any[] = [];
    let districts: any[] = [];
    if (isState5) {
      try {
        const [stateRes, eduRes]: any[] = await Promise.all([
          triggerState().unwrap(),
          triggerAllEduDist().unwrap(),
        ]);
        districts = [...(stateRes?.result ?? [])].sort((a: any, b: any) =>
          a.district_name > b.district_name ? -1 : 1
        );
        eduDist = [...(eduRes?.result ?? [])].sort((a: any, b: any) =>
          a.edudistName > b.edudistName ? -1 : 1
        );
        setAllDistrict(districts);
        setAllEduDist(eduDist);
      } catch {
        // non-fatal — bar chart just stays empty
      }
    }

    try {
      const res: any = await triggerNewCounselling().unwrap();
      if (!res?.dataStatus) {
        alertService.error(res.message);
        return;
      }

      const result: any[] = res.result ?? [];
      let filterList: any[];
      // ── Role/directorate branch table (ported verbatim) ──
      if (eq(userType, '5') && eq(userType1, '1')) {
        filterList = result.filter(
          (ft) => eq(ft.Status, '1') && eq(ft.Directorate, '1')
        ); // DEE
      } else if (eq(userType, '5') && eq(userType1, '2')) {
        filterList = result.filter(
          (ft) => eq(ft.Status, '1') && eq(ft.Directorate, '2')
        ); // DSE
      } else if (
        (eq(userType, '5') && eq(userType1, '3')) ||
        (eq(userType, '9') && !eq(userType1, '29'))
      ) {
        filterList = result.filter((ft) => eq(ft.Status, '1')); // STATE
      } else if (
        (eq(userType, '5') && eq(userType1, '29')) ||
        (eq(userType, '9') && eq(userType1, '29')) ||
        (eq(userType, '6') && eq(userType1, '29'))
      ) {
        filterList = result.filter(
          (ft) => eq(ft.Status, '1') && eq(ft.Directorate, '4')
        ); // GCC
      } else if (eq(userType, '5') && eq(userType1, '6')) {
        filterList = result.filter(
          (ft) => eq(ft.Status, '1') && eq(ft.Directorate, '5')
        );
      } else if (
        (eq(userType, '5') && eq(userType1, '8')) ||
        (eq(userType, '7') && eq(userType1, '7'))
      ) {
        filterList = result.filter(
          (ft) => eq(ft.Status, '1') && eq(ft.Directorate, '6')
        ); // Kallar schools
      } else {
        filterList = result.filter((ft) => eq(ft.Status, '1')); // Else
      }

      const list: CounsellingDropDown[] = filterList.map((mp) => ({
        label: mp.Title,
        value: mp.IndexId,
        directorate: mp.Directorate,
      }));
      setCounsellingList(list);

      // Guard: some roles have no active events — don't crash on list[0].
      if (list.length > 0) {
        setCounselling(list[0].value);
        handleCounselling(list[0].value, list, eduDist, districts);
      }
    } catch {
      alertService.error('Failed to load counselling list');
    }
  }, [
    isState5,
    userType,
    userType1,
    triggerNewCounselling,
    triggerState,
    triggerAllEduDist,
    handleCounselling,
  ]);

  // Run once the session is hydrated. On a hard refresh the Redux session is
  // restored asynchronously, so on the first render `usertypeid` is empty and
  // `isState5` is false — running then would skip the state-5 district/eduDist
  // fetch and leave the District-wise chart blank. Waiting for `userType` to be
  // populated (and guarding against a duplicate run) fixes the refresh case.
  const loadedRef = useRef(false);
  useEffect(() => {
    if (!userType || loadedRef.current) return;
    loadedRef.current = true;
    getAllNewCounselling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  // ── Chart option builders (ECharts options transfer near-verbatim) ─────────
  const gaugeOption = useMemo<EChartsCoreOption | null>(() => {
    if (dashboardCount.length === 0) return null;
    return {
      series: [
        {
          type: 'gauge',
          startAngle: 180,
          endAngle: 0,
          min: lastDigit,
          max: +dashboardCount[0].Candidates,
          splitNumber: 10,
          itemStyle: {
            color: '#58D9F9',
            shadowColor: 'rgba(0,138,255,0.45)',
            shadowBlur: 10,
            shadowOffsetX: 2,
            shadowOffsetY: 2,
          },
          progress: { show: true, roundCap: true, width: 18 },
          pointer: {
            icon: 'path://M2090.36389,615.30999 L2090.36389,615.30999 C2091.48372,615.30999 2092.40383,616.194028 2092.44859,617.312956 L2096.90698,728.755929 C2097.05155,732.369577 2094.2393,735.416212 2090.62566,735.56078 C2090.53845,735.564269 2090.45117,735.566014 2090.36389,735.566014 L2090.36389,735.566014 C2086.74736,735.566014 2083.81557,732.63423 2083.81557,729.017692 C2083.81557,728.930412 2083.81732,728.84314 2083.82081,728.755929 L2088.2792,617.312956 C2088.32396,616.194028 2089.24407,615.30999 2090.36389,615.30999 Z',
            length: '75%',
            width: 16,
            offsetCenter: [0, '5%'],
          },
          axisLine: { roundCap: true, lineStyle: { width: 18 } },
          axisTick: { splitNumber: 2, lineStyle: { width: 2, color: '#999' } },
          splitLine: { length: 12, lineStyle: { width: 3, color: '#999' } },
          axisLabel: { distance: 30, color: '#999', fontSize: 20 },
          title: { show: false },
          detail: {
            backgroundColor: '#fff',
            borderColor: '#999',
            borderWidth: 2,
            width: '60%',
            lineHeight: 40,
            height: 40,
            borderRadius: 8,
            offsetCenter: [0, '35%'],
            valueAnimation: true,
            rich: {
              value: { fontSize: 50, fontWeight: 'bolder', color: '#777' },
              unit: { fontSize: 20, color: '#999', padding: [0, 0, -20, 10] },
            },
          },
          data: [{ value: totalTaken }],
        },
      ],
    };
  }, [dashboardCount, lastDigit, totalTaken]);

  const pieOption = useMemo<EChartsCoreOption | null>(() => {
    if (pieChartData.length === 0) return null;
    return {
      color: ['#4472e0', '#67c23a', '#f2c037', '#e5545c'],
      title: { text: 'Summary', subtext: '', left: 'center' },
      tooltip: { trigger: 'item' },
      legend: { orient: 'vertical', top: 'top', left: 'left' },
      series: [
        {
          name: 'Candidate',
          type: 'pie',
          radius: '50%',
          label: {
            formatter: '{b|{b}: {c}}',
            rich: {
              b: { color: '#4C5058', fontSize: 14, fontWeight: 'bold' },
            },
          },
          data: pieChartData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    };
  }, [pieChartData]);

  const barOption = useMemo<EChartsCoreOption | null>(() => {
    if (!isState5 || axisArr.length === 0) return null;
    const labelOption = {
      show: true,
      formatter: '{c}%',
      rotate: 0,
      rich: { name: {} },
    };
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) return '0';
          const lines = params.map(
            (p: any) => p.marker + p.seriesName + ': ' + p.data.total
          );
          return params[0].name + '<br>' + lines.join('<br>');
        },
      },
      toolbox: {
        show: true,
        orient: 'vertical',
        left: 'right',
        top: 'center',
        feature: { saveAsImage: { show: true, name: 'Ditrict wise Data' } },
      },
      grid: {
        left: '4%',
        right: '4%',
        bottom: '4%',
        top: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        show: true,
        name: 'X-Axis',
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: {
          color: '#000000',
          fontWeight: 'bold',
          fontStyle: 'normal',
          fontFamily: 'Segoe UI',
          fontSize: 15,
        },
        axisLabel: {
          formatter: (value: number) => value.toLocaleString('en-IN'),
        },
      },
      yAxis: {
        type: 'category',
        axisTick: { show: false },
        data: axisArr,
        show: true,
        nameGap: 160,
        name: 'Y-Axis',
        axisLabel: { padding: [10, 10, 10, 10] },
        nameTextStyle: {
          color: '#000000',
          fontWeight: 'bold',
          fontStyle: 'normal',
          fontFamily: 'Segoe UI',
          fontSize: 15,
        },
        nameLocation: 'middle',
      },
      series: [
        {
          name: 'Pending',
          type: 'bar',
          stack: 'total',
          label: labelOption,
          emphasis: { focus: 'series' },
          data: totalPendingArr,
        },
        {
          name: 'Order Copy',
          type: 'bar',
          stack: 'total',
          label: labelOption,
          emphasis: { focus: 'series' },
          data: totalOrderArr,
        },
        {
          name: 'Not Willing',
          type: 'bar',
          stack: 'total',
          label: labelOption,
          emphasis: { focus: 'series' },
          data: totalNotWillingArr,
        },
        {
          name: 'Absent',
          type: 'bar',
          stack: 'total',
          label: labelOption,
          emphasis: { focus: 'series' },
          data: totalAbsentArr,
        },
      ],
      legend: { show: true, bottom: -5 },
    };
  }, [
    isState5,
    axisArr,
    totalPendingArr,
    totalOrderArr,
    totalNotWillingArr,
    totalAbsentArr,
  ]);

  // ── Chart refs (one instance each, disposed on unmount) ────────────────────
  const gaugeRef = useECharts(gaugeOption);
  const pieRef = useECharts(pieOption);
  const barRef = useECharts(barOption, isState5);

  const onChangeCounselling = (value: any) => {
    setCounselling(value);
    handleCounselling(value, counsellingList, allEduDist, allDistrict);
  };

  return (
    <div className="counselling-dashboard main-body-content">
      <div className="row">
        <div className="col text-center">
          <h5>
            <button
              className="btn button-color civil-but headingButton menubtn"
              type="button"
            >
              Dashboard
            </button>
          </h5>
        </div>
      </div>

      <div className="p-card p-3">
        <div className="row">
          <div className="col-3">
            <b>Counselling</b>
            <Dropdown
              value={counselling}
              options={counsellingList}
              optionLabel="label"
              optionValue="value"
              filter
              filterBy="label"
              placeholder="Select a counselling"
              appendTo="self"
              className="w-100"
              onChange={(e) => onChangeCounselling(e.value)}
            />
          </div>
        </div>
        <br />

        <div className="row">
          <div className="col-6">
            <div className="text-center">
              <b>Counselling</b>
            </div>
            <br />
            <div
              ref={gaugeRef}
              id="protector_chart"
              style={{ width: '100%', height: '600px' }}
            />
          </div>
          <div className="col-6">
            <div className="text-center" />
            <div
              ref={pieRef}
              id="pie_chart"
              style={{ width: '100%', height: '500px' }}
            />
          </div>
        </div>

        {isState5 && (
          <div className="text-center mb-3">
            <b>{chartHeader}</b>
            <div
              ref={barRef}
              id="bar_chart"
              style={{ width: '100%', height: '1800px' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CounsellingDashboard;

/* eslint-enable @typescript-eslint/no-explicit-any */
